/* ==========================================================================
   Search System - The Arcanopolis Archive
   Advanced search functionality with Fuse.js integration
   ========================================================================== */

const SearchSystem = {
    searchIndex: null,
    recentSearches: [],
    searchHistory: [],
    
    // Initialize search system
    init() {
        this.loadRecentSearches();
        this.setupSearchInterface();
        this.loadSearchFromURL();
    },
    
    // Setup search interface
    setupSearchInterface() {
        const searchInput = document.getElementById('searchInput');
        const searchButton = document.getElementById('searchButton');
        const filterToggle = document.getElementById('filterToggle');
        const filterPanel = document.getElementById('filterPanel');
        const clearFilters = document.getElementById('clearFilters');
        
        if (searchInput && searchButton) {
            searchButton.addEventListener('click', () => this.performSearch());
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.performSearch();
            });
            
            // Real-time search suggestions
            searchInput.addEventListener('input', (e) => {
                this.showSearchSuggestions(e.target.value);
            });
            
            // Clear search
            searchInput.addEventListener('focus', () => {
                if (searchInput.value) {
                    this.showSearchSuggestions(searchInput.value);
                }
            });
        }
        
        if (filterToggle && filterPanel) {
            filterToggle.addEventListener('click', () => {
                const isVisible = filterPanel.style.display !== 'none';
                filterPanel.style.display = isVisible ? 'none' : 'block';
                filterToggle.classList.toggle('active');
            });
        }
        
        if (clearFilters) {
            clearFilters.addEventListener('click', () => this.clearAllFilters());
        }
        
        // Setup filter controls
        this.setupFilterControls();
    },
    
    // Setup filter controls
    setupFilterControls() {
        // Category filter
        const categoryFilter = document.getElementById('categoryFilter');
        if (categoryFilter) {
            categoryFilter.addEventListener('change', () => this.updateSearchResults());
        }
        
        // Tag filter
        const tagFilter = document.getElementById('tagFilter');
        if (tagFilter) {
            tagFilter.addEventListener('change', () => this.updateSearchResults());
        }
        
        // Date range filter
        const dateFrom = document.getElementById('dateFrom');
        const dateTo = document.getElementById('dateTo');
        if (dateFrom) {
            dateFrom.addEventListener('change', () => this.updateSearchResults());
        }
        if (dateTo) {
            dateTo.addEventListener('change', () => this.updateSearchResults());
        }
        
        // Sort options
        const sortSelect = document.getElementById('sortSelect');
        if (sortSelect) {
            sortSelect.addEventListener('change', () => this.updateSearchResults());
        }
    },
    
    // Load search from URL parameters
    loadSearchFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('q');
        const category = urlParams.get('category');
        const tags = urlParams.get('tags');
        
        if (query) {
            const searchInput = document.getElementById('searchInput');
            if (searchInput) {
                searchInput.value = query;
                this.performSearch();
            }
        }
        
        if (category) {
            const categoryFilter = document.getElementById('categoryFilter');
            if (categoryFilter) {
                categoryFilter.value = category;
            }
        }
        
        if (tags) {
            const tagFilter = document.getElementById('tagFilter');
            if (tagFilter) {
                tagFilter.value = tags;
            }
        }
    },
    
    // Perform search
    performSearch() {
        const searchInput = document.getElementById('searchInput');
        if (!searchInput) return;
        
        const query = searchInput.value.trim();
        if (!query) {
            this.clearSearchResults();
            return;
        }
        
        this.showSearchResults(query);
        this.saveRecentSearch(query);
        this.updateURL(query);
    },
    
    // Show search results
    showSearchResults(query) {
        const resultsContainer = document.getElementById('resultsContainer');
        const resultsHeader = document.getElementById('resultsHeader');
        const resultsTitle = document.getElementById('resultsTitle');
        const resultsCount = document.getElementById('resultsCount');
        
        if (!resultsContainer) return;
        
        let results = this.searchContent(query);
        
        if (results.length === 0) {
            resultsContainer.innerHTML = `
                <div class="search-welcome">
                    <div class="welcome-icon">🔍</div>
                    <h3>No Results Found</h3>
                    <p>No entries match your search for "${query}". Try different keywords or check your spelling.</p>
                    <div class="search-suggestions">
                        <h4>Search Tips:</h4>
                        <ul>
                            <li>Try broader or different keywords</li>
                            <li>Check your spelling</li>
                            <li>Use fewer words</li>
                            <li>Try searching by category or tags</li>
                        </ul>
                    </div>
                </div>
            `;
        } else {
            resultsContainer.innerHTML = results.map(item => 
                App.createContentCard(item)
            ).join('');
        }
        
        // Update results header
        if (resultsHeader) resultsHeader.style.display = 'block';
        if (resultsTitle) resultsTitle.textContent = `Search Results for "${query}"`;
        if (resultsCount) resultsCount.textContent = `${results.length} ${results.length === 1 ? 'result' : 'results'} found`;
        
        // Highlight search terms
        this.highlightSearchTerms(query, resultsContainer);
    },
    
    // Search content using Fuse.js or fallback
    searchContent(query) {
        let results = [];
        
        if (App.searchIndex) {
            const fuseResults = App.searchIndex.search(query);
            results = fuseResults.map(result => result.item);
        } else {
            // Fallback simple search
            results = this.simpleSearch(query);
        }
        
        // Apply filters
        results = this.applyFilters(results);
        
        // Apply sorting
        results = this.applySorting(results);
        
        return results;
    },
    
    // Simple search fallback
    simpleSearch(query) {
        const allItems = [];
        for (const [category, items] of App.data) {
            allItems.push(...items.map(item => ({ ...item, category })));
        }
        
        const lowerQuery = query.toLowerCase();
        return allItems.filter(item => 
            (item.title && item.title.toLowerCase().includes(lowerQuery)) ||
            (item.rawContent && item.rawContent.toLowerCase().includes(lowerQuery)) ||
            (item.tags && item.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
        );
    },
    
    // Apply filters to search results
    applyFilters(results) {
        const categoryFilter = document.getElementById('categoryFilter');
        const tagFilter = document.getElementById('tagFilter');
        const dateFrom = document.getElementById('dateFrom');
        const dateTo = document.getElementById('dateTo');
        
        let filtered = results;
        
        // Category filter
        if (categoryFilter && categoryFilter.value && categoryFilter.value !== 'all') {
            filtered = filtered.filter(item => item.category === categoryFilter.value);
        }
        
        // Tag filter
        if (tagFilter && tagFilter.value) {
            const selectedTags = tagFilter.value.split(',').map(tag => tag.trim());
            filtered = filtered.filter(item => 
                item.tags && item.tags.some(tag => selectedTags.includes(tag))
            );
        }
        
        // Date range filter
        if (dateFrom && dateFrom.value) {
            const fromDate = new Date(dateFrom.value);
            filtered = filtered.filter(item => {
                if (!item.date) return false;
                return new Date(item.date) >= fromDate;
            });
        }
        
        if (dateTo && dateTo.value) {
            const toDate = new Date(dateTo.value);
            filtered = filtered.filter(item => {
                if (!item.date) return false;
                return new Date(item.date) <= toDate;
            });
        }
        
        return filtered;
    },
    
    // Apply sorting to search results
    applySorting(results) {
        const sortSelect = document.getElementById('sortSelect');
        if (!sortSelect) return results;
        
        const sortBy = sortSelect.value;
        
        return results.sort((a, b) => {
            switch (sortBy) {
                case 'title-asc':
                    return a.title.localeCompare(b.title);
                case 'title-desc':
                    return b.title.localeCompare(a.title);
                case 'date-new':
                    return new Date(b.date || 0) - new Date(a.date || 0);
                case 'date-old':
                    return new Date(a.date || 0) - new Date(b.date || 0);
                case 'category':
                    return a.category.localeCompare(b.category);
                case 'relevance':
                    // For relevance, maintain original order (Fuse.js already sorted by relevance)
                    return 0;
                default:
                    return 0;
            }
        });
    },
    
    // Update search results when filters change
    updateSearchResults() {
        const searchInput = document.getElementById('searchInput');
        if (searchInput && searchInput.value.trim()) {
            this.showSearchResults(searchInput.value.trim());
        }
    },
    
    // Clear all filters
    clearAllFilters() {
        const categoryFilter = document.getElementById('categoryFilter');
        const tagFilter = document.getElementById('tagFilter');
        const dateFrom = document.getElementById('dateFrom');
        const dateTo = document.getElementById('dateTo');
        const sortSelect = document.getElementById('sortSelect');
        
        if (categoryFilter) categoryFilter.value = 'all';
        if (tagFilter) tagFilter.value = '';
        if (dateFrom) dateFrom.value = '';
        if (dateTo) dateTo.value = '';
        if (sortSelect) sortSelect.value = 'relevance';
        
        this.updateSearchResults();
    },
    
    // Show search suggestions
    showSearchSuggestions(query) {
        const suggestionsContainer = document.getElementById('searchSuggestions');
        if (!suggestionsContainer || query.length < 2) {
            if (suggestionsContainer) suggestionsContainer.style.display = 'none';
            return;
        }
        
        const suggestions = this.getSearchSuggestions(query);
        
        if (suggestions.length === 0) {
            suggestionsContainer.style.display = 'none';
            return;
        }
        
        suggestionsContainer.innerHTML = suggestions.map(suggestion => 
            `<div class="suggestion-item" data-query="${suggestion}">${suggestion}</div>`
        ).join('');
        
        suggestionsContainer.style.display = 'block';
        
        // Add click handlers
        suggestionsContainer.querySelectorAll('.suggestion-item').forEach(item => {
            item.addEventListener('click', () => {
                const searchInput = document.getElementById('searchInput');
                if (searchInput) {
                    searchInput.value = item.dataset.query;
                    this.performSearch();
                }
            });
        });
    },
    
    // Get search suggestions
    getSearchSuggestions(query) {
        const allItems = [];
        for (const [category, items] of App.data) {
            allItems.push(...items);
        }
        
        const lowerQuery = query.toLowerCase();
        const suggestions = new Set();
        
        allItems.forEach(item => {
            if (item.title && item.title.toLowerCase().includes(lowerQuery)) {
                suggestions.add(item.title);
            }
            if (item.tags) {
                item.tags.forEach(tag => {
                    if (tag.toLowerCase().includes(lowerQuery)) {
                        suggestions.add(tag);
                    }
                });
            }
        });
        
        return Array.from(suggestions).slice(0, 5);
    },
    
    // Highlight search terms in results
    highlightSearchTerms(query, container) {
        const terms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
        
        if (terms.length === 0) return;
        
        const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );
        
        const textNodes = [];
        let node;
        
        while (node = walker.nextNode()) {
            textNodes.push(node);
        }
        
        textNodes.forEach(textNode => {
            let text = textNode.textContent;
            let highlighted = false;
            
            terms.forEach(term => {
                const regex = new RegExp(`(${term})`, 'gi');
                if (regex.test(text)) {
                    text = text.replace(regex, '<mark>$1</mark>');
                    highlighted = true;
                }
            });
            
            if (highlighted) {
                const wrapper = document.createElement('span');
                wrapper.innerHTML = text;
                textNode.parentNode.replaceChild(wrapper, textNode);
            }
        });
    },
    
    // Save recent search
    saveRecentSearch(query) {
        this.recentSearches = this.recentSearches.filter(search => search !== query);
        this.recentSearches.unshift(query);
        this.recentSearches = this.recentSearches.slice(0, 5);
        
        localStorage.setItem('arcanopolis-recent-searches', JSON.stringify(this.recentSearches));
        this.updateRecentSearches();
    },
    
    // Load recent searches from localStorage
    loadRecentSearches() {
        this.recentSearches = JSON.parse(localStorage.getItem('arcanopolis-recent-searches') || '[]');
    },
    
    // Update recent searches display
    updateRecentSearches() {
        const recentSearches = document.getElementById('recentSearches');
        const recentSearchChips = document.getElementById('recentSearchChips');
        
        if (!recentSearches || !recentSearchChips) return;
        
        if (this.recentSearches.length === 0) {
            recentSearches.style.display = 'none';
            return;
        }
        
        recentSearchChips.innerHTML = this.recentSearches.map(search => 
            `<button class="search-chip" data-query="${search}">${search}</button>`
        ).join('');
        
        recentSearches.style.display = 'block';
        
        // Add click handlers
        recentSearchChips.querySelectorAll('.search-chip').forEach(chip => {
            chip.addEventListener('click', () => {
                const searchInput = document.getElementById('searchInput');
                if (searchInput) {
                    searchInput.value = chip.dataset.query;
                    this.performSearch();
                }
            });
        });
    },
    
    // Update URL with search parameters
    updateURL(query) {
        const url = new URL(window.location);
        url.searchParams.set('q', query);
        
        const categoryFilter = document.getElementById('categoryFilter');
        if (categoryFilter && categoryFilter.value && categoryFilter.value !== 'all') {
            url.searchParams.set('category', categoryFilter.value);
        } else {
            url.searchParams.delete('category');
        }
        
        const tagFilter = document.getElementById('tagFilter');
        if (tagFilter && tagFilter.value) {
            url.searchParams.set('tags', tagFilter.value);
        } else {
            url.searchParams.delete('tags');
        }
        
        window.history.pushState({}, '', url);
    },
    
    // Clear search results
    clearSearchResults() {
        const resultsContainer = document.getElementById('resultsContainer');
        const resultsHeader = document.getElementById('resultsHeader');
        
        if (resultsContainer) {
            resultsContainer.innerHTML = `
                <div class="search-welcome">
                    <div class="welcome-icon">🔍</div>
                    <h3>Search the Archive</h3>
                    <p>Enter your search terms above to find content in the Arcanopolis Archive.</p>
                </div>
            `;
        }
        
        if (resultsHeader) {
            resultsHeader.style.display = 'none';
        }
    }
};

// Export for use in other modules
window.SearchSystem = SearchSystem;